// AVB1: Avanaspace product-billing (Phase 1). Self-contained; mounts under Accounts.
import React, { useState, useEffect, useCallback } from 'react';
import client from '../api/client';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Plus, Trash2, Edit2, FileText, Users, Package,
  Printer, X, Search, IndianRupee, Wallet, Receipt,
} from 'lucide-react';

const CO = 2; // Avanaspace
const n = (v) => { const x = parseFloat(v); return isNaN(x) ? 0 : x; };
const r2 = (v) => Math.round((n(v) + Number.EPSILON) * 100) / 100;
const fmt = (v) => n(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);

// mirror backend computeTotals for live preview
function calc(items, party) {
  const gstin = String((party && party.gstin) || '').trim();
  const inter = gstin.length >= 2 && gstin.slice(0, 2) !== '33';
  let taxable = 0, gst = 0, gross = 0, disc = 0;
  items.forEach((it) => {
    const g = r2(n(it.qty) * n(it.price));
    const d = r2(g * n(it.discount_pct) / 100);
    const base = r2(g - d);
    gross += g; disc += d; taxable += base; gst += r2(base * n(it.gst_rate) / 100);
  });
  return {
    inter, subtotal: r2(gross), discount_total: r2(disc), taxable: r2(taxable),
    cgst: inter ? 0 : r2(gst / 2), sgst: inter ? 0 : r2(gst / 2), igst: inter ? r2(gst) : 0,
    grand_total: r2(taxable + gst),
  };
}

export default function AvBilling({ onBack }) {
  const [view, setView] = useState('home');
  const [ledgerParty, setLedgerParty] = useState(null);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => (view === 'home' ? onBack() : setView('home'))}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"><ArrowLeft size={20} /></button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Avanaspace Products — Billing</h1>
          <p className="text-xs text-gray-500">Product sales invoicing & party ledger</p>
        </div>
      </div>

      {view === 'home' && <Home go={setView} openLedger={(p) => { setLedgerParty(p); setView('ledger'); }} />}
      {view === 'invoiceNew' && <InvoiceForm onDone={() => setView('invoices')} />}
      {view === 'invoices' && <Invoices go={setView} />}
      {view === 'items' && <Items />}
      {view === 'parties' && <Parties openLedger={(p) => { setLedgerParty(p); setView('ledger'); }} />}
      {view === 'ledger' && <Ledger party={ledgerParty} back={() => setView('parties')} />}
    </div>
  );
}

/* ───────── HOME / DASHBOARD ───────── */
function Home({ go, openLedger }) {
  const [s, setS] = useState({ toCollect: 0, toPay: 0, partiesCount: 0, invoicesCount: 0 });
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    client.get(`/av-billing/summary?company_id=${CO}`).then((r) => setS(r.data)).catch(() => {});
    client.get(`/av-billing/invoices?company_id=${CO}`).then((r) => setRecent((r.data.invoices || []).slice(0, 6))).catch(() => {});
  }, []);
  const Tile = ({ icon, label, onClick }) => (
    <button onClick={onClick} className="flex flex-col items-center justify-center gap-2 bg-white border border-gray-200 rounded-xl p-5 hover:border-indigo-400 hover:shadow-sm transition">
      <div className="text-indigo-600">{icon}</div>
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </button>
  );
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-xs text-emerald-700">To Collect</p>
          <p className="text-xl font-bold text-emerald-800">₹{fmt(s.toCollect)}</p>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
          <p className="text-xs text-rose-700">To Pay</p>
          <p className="text-xl font-bold text-rose-800">₹{fmt(s.toPay)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500">Parties</p>
          <p className="text-xl font-bold text-gray-800">{s.partiesCount}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500">Invoices</p>
          <p className="text-xl font-bold text-gray-800">{s.invoicesCount}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Tile icon={<Plus size={26} />} label="New Sales Invoice" onClick={() => go('invoiceNew')} />
        <Tile icon={<FileText size={26} />} label="Invoices" onClick={() => go('invoices')} />
        <Tile icon={<Users size={26} />} label="Parties" onClick={() => go('parties')} />
        <Tile icon={<Package size={26} />} label="Items" onClick={() => go('items')} />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-4 py-3 border-b text-sm font-semibold text-gray-700">Recent Invoices</div>
        {recent.length === 0 ? <div className="p-6 text-center text-sm text-gray-400">No invoices yet</div> : (
          <table className="w-full text-sm">
            <tbody>
              {recent.map((i) => (
                <tr key={i.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium text-gray-800">{i.invoice_no}</td>
                  <td className="px-4 py-2 text-gray-600">{(i.party_snapshot && i.party_snapshot.name) || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">{i.invoice_date}</td>
                  <td className="px-4 py-2 text-right font-semibold">₹{fmt(i.grand_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ───────── ITEMS ───────── */
function Items() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null); // item being edited, or {} for new
  const load = useCallback(() => client.get(`/av-billing/items?company_id=${CO}`).then((r) => setRows(r.data.items || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async (f) => {
    try {
      if (!f.name.trim()) return toast.error('Name required');
      if (f.id) await client.put(`/av-billing/items/${f.id}`, f); else await client.post('/av-billing/items', { ...f, company_id: CO });
      toast.success('Saved'); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const del = async (id) => { if (!window.confirm('Delete this item?')) return; try { await client.delete(`/av-billing/items/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Items ({rows.length})</h2>
        <button onClick={() => setModal({ name: '', hsn: '', sales_price: '', gst_rate: 18, unit: 'PCS' })} className="flex items-center gap-1 bg-indigo-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-indigo-700"><Plus size={16} /> Add Item</button>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs"><tr>
            <th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">HSN</th>
            <th className="text-right px-4 py-2">Price</th><th className="text-center px-4 py-2">GST%</th>
            <th className="text-center px-4 py-2">Unit</th><th className="px-4 py-2"></th></tr></thead>
          <tbody>
            {rows.map((it) => (
              <tr key={it.id} className="border-t">
                <td className="px-4 py-2 font-medium text-gray-800">{it.name}</td>
                <td className="px-4 py-2 text-gray-500">{it.hsn || '—'}</td>
                <td className="px-4 py-2 text-right">₹{fmt(it.sales_price)}</td>
                <td className="px-4 py-2 text-center">{n(it.gst_rate)}%</td>
                <td className="px-4 py-2 text-center">{it.unit}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setModal(it)} className="p-1 text-gray-400 hover:text-indigo-600"><Edit2 size={15} /></button>
                  <button onClick={() => del(it.id)} className="p-1 text-gray-400 hover:text-rose-600"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gray-400">No items</td></tr>}
          </tbody>
        </table>
      </div>
      {modal && <ItemModal init={modal} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}
function ItemModal({ init, onClose, onSave }) {
  const [f, setF] = useState(init);
  const u = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Modal title={f.id ? 'Edit Item' : 'New Item'} onClose={onClose}>
      <Field label="Item Name *"><input className="inp" value={f.name} onChange={(e) => u('name', e.target.value)} autoFocus /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="HSN / SAC"><input className="inp" value={f.hsn || ''} onChange={(e) => u('hsn', e.target.value)} /></Field>
        <Field label="Unit"><input className="inp" value={f.unit || ''} onChange={(e) => u('unit', e.target.value)} /></Field>
        <Field label="Sales Price (₹)"><input type="number" className="inp" value={f.sales_price} onChange={(e) => u('sales_price', e.target.value)} /></Field>
        <Field label="GST %"><input type="number" className="inp" value={f.gst_rate} onChange={(e) => u('gst_rate', e.target.value)} /></Field>
      </div>
      <button onClick={() => onSave(f)} className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 mt-2">Save</button>
    </Modal>
  );
}

/* ───────── PARTIES ───────── */
function Parties({ openLedger }) {
  const [rows, setRows] = useState([]);
  const [sum, setSum] = useState({ toCollect: 0, toPay: 0 });
  const [modal, setModal] = useState(null);
  const [q, setQ] = useState('');
  const load = useCallback(() => client.get(`/av-billing/parties?company_id=${CO}`).then((r) => { setRows(r.data.parties || []); setSum(r.data.summary || {}); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async (f) => {
    try {
      if (!f.name.trim()) return toast.error('Name required');
      if (f.id) await client.put(`/av-billing/parties/${f.id}`, f); else await client.post('/av-billing/parties', { ...f, company_id: CO });
      toast.success('Saved'); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const del = async (id) => { if (!window.confirm('Delete this party?')) return; try { await client.delete(`/av-billing/parties/${id}`); toast.success('Deleted'); load(); } catch (e) { toast.error(e.response?.data?.error || 'Failed'); } };
  const filtered = rows.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()) || (p.mobile || '').includes(q));
  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-3">
        <h2 className="text-lg font-semibold text-gray-800">Parties ({rows.length})</h2>
        <button onClick={() => setModal({ name: '', mobile: '', gstin: '', pan: '', email: '', billing_address: '', party_type: 'customer', opening_balance: 0 })} className="flex items-center gap-1 bg-indigo-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-indigo-700"><Plus size={16} /> Add Party</button>
      </div>
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
        <input className="inp pl-9" placeholder="Search name or mobile…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs"><tr>
            <th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Mobile</th>
            <th className="text-left px-4 py-2">GSTIN</th><th className="text-right px-4 py-2">Balance</th><th className="px-4 py-2"></th></tr></thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-800 cursor-pointer" onClick={() => openLedger(p)}>{p.name}<span className="ml-2 text-[10px] uppercase text-gray-400">{p.party_type}</span></td>
                <td className="px-4 py-2 text-gray-500">{p.mobile || '—'}</td>
                <td className="px-4 py-2 text-gray-500">{p.gstin || '—'}</td>
                <td className={`px-4 py-2 text-right font-semibold ${n(p.balance) > 0 ? 'text-emerald-700' : n(p.balance) < 0 ? 'text-rose-700' : 'text-gray-500'}`}>
                  ₹{fmt(Math.abs(n(p.balance)))}<span className="ml-1 text-[10px]">{n(p.balance) > 0 ? 'Dr' : n(p.balance) < 0 ? 'Cr' : ''}</span>
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setModal(p)} className="p-1 text-gray-400 hover:text-indigo-600"><Edit2 size={15} /></button>
                  <button onClick={() => del(p.id)} className="p-1 text-gray-400 hover:text-rose-600"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-400">No parties</td></tr>}
          </tbody>
        </table>
      </div>
      {modal && <PartyModal init={modal} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}
function PartyModal({ init, onClose, onSave }) {
  const [f, setF] = useState(init);
  const u = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Modal title={f.id ? 'Edit Party' : 'New Party'} onClose={onClose}>
      <Field label="Party Name *"><input className="inp" value={f.name} onChange={(e) => u('name', e.target.value)} autoFocus /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Mobile"><input className="inp" value={f.mobile || ''} onChange={(e) => u('mobile', e.target.value)} /></Field>
        <Field label="Type">
          <select className="inp" value={f.party_type} onChange={(e) => u('party_type', e.target.value)}>
            <option value="customer">Customer</option><option value="supplier">Supplier</option>
          </select>
        </Field>
        <Field label="GSTIN"><input className="inp uppercase" value={f.gstin || ''} onChange={(e) => u('gstin', e.target.value)} /></Field>
        <Field label="PAN"><input className="inp uppercase" value={f.pan || ''} onChange={(e) => u('pan', e.target.value)} /></Field>
      </div>
      <Field label="Email"><input className="inp" value={f.email || ''} onChange={(e) => u('email', e.target.value)} /></Field>
      <Field label="Billing Address"><textarea className="inp" rows={2} value={f.billing_address || ''} onChange={(e) => u('billing_address', e.target.value)} /></Field>
      <Field label="Opening Balance (₹, +ve = they owe you)"><input type="number" className="inp" value={f.opening_balance} onChange={(e) => u('opening_balance', e.target.value)} /></Field>
      <button onClick={() => onSave(f)} className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 mt-2">Save</button>
    </Modal>
  );
}

/* ───────── INVOICE FORM ───────── */
function blankLine() { return { name: '', hsn: '', qty: 1, unit: 'PCS', price: '', discount_pct: 0, gst_rate: 18 }; }
function InvoiceForm({ onDone }) {
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [partyId, setPartyId] = useState('');
  const [date, setDate] = useState(today());
  const [lines, setLines] = useState([blankLine()]);
  const [received, setReceived] = useState('');
  const [mode, setMode] = useState('Cash');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    client.get(`/av-billing/parties?company_id=${CO}`).then((r) => setParties(r.data.parties || [])).catch(() => {});
    client.get(`/av-billing/items?company_id=${CO}`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);
  const party = parties.find((p) => p.id === partyId);
  const t = calc(lines, party);
  const setLine = (i, k, v) => setLines((p) => p.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const pickItem = (i, itemId) => {
    const it = items.find((x) => x.id === itemId); if (!it) return;
    setLines((p) => p.map((l, idx) => (idx === i ? { ...l, name: it.name, hsn: it.hsn || '', price: it.sales_price, gst_rate: it.gst_rate, unit: it.unit } : l)));
  };
  const save = async () => {
    if (!partyId) return toast.error('Select a party');
    if (!lines.some((l) => l.name.trim())) return toast.error('Add at least one item');
    setSaving(true);
    try {
      const r = await client.post('/av-billing/invoices', { company_id: CO, party_id: partyId, invoice_date: date, items: lines.filter((l) => l.name.trim()), amount_received: n(received), payment_mode: mode, notes });
      toast.success('Invoice ' + r.data.invoice.invoice_no + ' saved');
      printInvoice(r.data.invoice);
      onDone();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-800 mb-4">New Sales Invoice</h2>
      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <Field label="Party *">
          <select className="inp" value={partyId} onChange={(e) => setPartyId(e.target.value)}>
            <option value="">— Select party —</option>
            {parties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.mobile ? ` (${p.mobile})` : ''}</option>)}
          </select>
        </Field>
        <Field label="Invoice Date"><input type="date" className="inp" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Invoice No"><input className="inp bg-gray-50" value="Auto on save" disabled /></Field>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto mb-4">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-gray-50 text-gray-500 text-xs"><tr>
            <th className="text-left px-2 py-2 w-1/3">Item</th><th className="px-2 py-2">HSN</th>
            <th className="px-2 py-2">Qty</th><th className="px-2 py-2">Price</th><th className="px-2 py-2">Disc%</th>
            <th className="px-2 py-2">GST%</th><th className="text-right px-2 py-2">Amount</th><th></th></tr></thead>
          <tbody>
            {lines.map((l, i) => {
              const g = r2(n(l.qty) * n(l.price)); const d = r2(g * n(l.discount_pct) / 100); const base = r2(g - d); const amt = r2(base + base * n(l.gst_rate) / 100);
              return (
                <tr key={i} className="border-t">
                  <td className="px-2 py-1">
                    <input className="inp mb-1" list={`items-${i}`} placeholder="Type or pick…" value={l.name} onChange={(e) => setLine(i, 'name', e.target.value)} />
                    <select className="inp text-xs text-gray-500" value="" onChange={(e) => pickItem(i, e.target.value)}>
                      <option value="">pick saved item…</option>
                      {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1"><input className="inp w-16" value={l.hsn} onChange={(e) => setLine(i, 'hsn', e.target.value)} /></td>
                  <td className="px-2 py-1"><input type="number" className="inp w-16" value={l.qty} onChange={(e) => setLine(i, 'qty', e.target.value)} /></td>
                  <td className="px-2 py-1"><input type="number" className="inp w-20" value={l.price} onChange={(e) => setLine(i, 'price', e.target.value)} /></td>
                  <td className="px-2 py-1"><input type="number" className="inp w-16" value={l.discount_pct} onChange={(e) => setLine(i, 'discount_pct', e.target.value)} /></td>
                  <td className="px-2 py-1"><input type="number" className="inp w-16" value={l.gst_rate} onChange={(e) => setLine(i, 'gst_rate', e.target.value)} /></td>
                  <td className="px-2 py-1 text-right font-medium">₹{fmt(amt)}</td>
                  <td className="px-2 py-1"><button onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-rose-600"><Trash2 size={15} /></button></td>
                  <datalist id={`items-${i}`}>{items.map((it) => <option key={it.id} value={it.name} />)}</datalist>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="p-2"><button onClick={() => setLines((p) => [...p, blankLine()])} className="flex items-center gap-1 text-indigo-600 text-sm px-2 py-1 hover:bg-indigo-50 rounded"><Plus size={15} /> Add line</button></div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <Field label="Amount Received (₹)"><input type="number" className="inp" value={received} onChange={(e) => setReceived(e.target.value)} placeholder="0.00" /></Field>
          <Field label="Payment Mode">
            <select className="inp" value={mode} onChange={(e) => setMode(e.target.value)}>
              {['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'Card'].map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Notes"><textarea className="inp" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm space-y-1 h-fit">
          <Row k="Taxable" v={t.taxable} />
          {t.discount_total > 0 && <Row k="Discount" v={t.discount_total} />}
          {!t.inter && <><Row k="CGST" v={t.cgst} /><Row k="SGST" v={t.sgst} /></>}
          {t.inter && <Row k="IGST" v={t.igst} />}
          <div className="flex justify-between border-t pt-2 mt-1 text-base font-bold text-gray-900"><span>Grand Total</span><span>₹{fmt(t.grand_total)}</span></div>
          <div className="flex justify-between text-emerald-700"><span>Received</span><span>₹{fmt(received)}</span></div>
          <div className="flex justify-between font-semibold text-rose-700"><span>Balance</span><span>₹{fmt(t.grand_total - n(received))}</span></div>
        </div>
      </div>

      <div className="flex gap-3 mt-5">
        <button disabled={saving} onClick={save} className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50"><Receipt size={18} /> {saving ? 'Saving…' : 'Save & Print'}</button>
        <button onClick={onDone} className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
      </div>
    </div>
  );
}
const Row = ({ k, v }) => <div className="flex justify-between text-gray-600"><span>{k}</span><span>₹{fmt(v)}</span></div>;

/* ───────── INVOICES LIST ───────── */
function Invoices({ go }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { client.get(`/av-billing/invoices?company_id=${CO}`).then((r) => setRows(r.data.invoices || [])).catch(() => {}); }, []);
  const del = async (id) => { if (!window.confirm('Delete this invoice?')) return; try { await client.delete(`/av-billing/invoices/${id}`); toast.success('Deleted'); setRows((p) => p.filter((x) => x.id !== id)); } catch { toast.error('Failed'); } };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Invoices ({rows.length})</h2>
        <button onClick={() => go('invoiceNew')} className="flex items-center gap-1 bg-indigo-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-indigo-700"><Plus size={16} /> New Invoice</button>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs"><tr>
            <th className="text-left px-4 py-2">Invoice #</th><th className="text-left px-4 py-2">Date</th>
            <th className="text-left px-4 py-2">Party</th><th className="text-right px-4 py-2">Total</th>
            <th className="text-right px-4 py-2">Received</th><th className="px-4 py-2"></th></tr></thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.id} className="border-t">
                <td className="px-4 py-2 font-medium text-gray-800">{i.invoice_no}</td>
                <td className="px-4 py-2 text-gray-500">{i.invoice_date}</td>
                <td className="px-4 py-2 text-gray-600">{(i.party_snapshot && i.party_snapshot.name) || '—'}</td>
                <td className="px-4 py-2 text-right font-semibold">₹{fmt(i.grand_total)}</td>
                <td className="px-4 py-2 text-right text-emerald-700">₹{fmt(i.amount_received)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => printInvoice(i)} className="p-1 text-gray-400 hover:text-indigo-600"><Printer size={15} /></button>
                  <button onClick={() => del(i.id)} className="p-1 text-gray-400 hover:text-rose-600"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gray-400">No invoices</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ───────── LEDGER ───────── */
function Ledger({ party, back }) {
  const [data, setData] = useState({ party: party || {}, rows: [] });
  useEffect(() => { if (party) client.get(`/av-billing/parties/${party.id}/ledger`).then((r) => setData(r.data)).catch(() => {}); }, [party]);
  if (!party) return null;
  const bal = n(data.party.balance);
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={back} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"><ArrowLeft size={18} /></button>
        <div><h2 className="text-lg font-semibold text-gray-800">{data.party.name}</h2><p className="text-xs text-gray-500">{data.party.mobile} · {data.party.gstin || 'No GSTIN'}</p></div>
        <div className="ml-auto text-right"><p className="text-xs text-gray-500">Balance</p><p className={`text-lg font-bold ${bal > 0 ? 'text-emerald-700' : bal < 0 ? 'text-rose-700' : 'text-gray-600'}`}>₹{fmt(Math.abs(bal))} {bal > 0 ? 'Dr' : bal < 0 ? 'Cr' : ''}</p></div>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs"><tr>
            <th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Particulars</th>
            <th className="text-right px-4 py-2">Debit</th><th className="text-right px-4 py-2">Credit</th><th className="text-right px-4 py-2">Balance</th></tr></thead>
          <tbody>
            {data.rows.map((rw, idx) => (
              <tr key={idx} className="border-t">
                <td className="px-4 py-2 text-gray-500">{rw.date || '—'}</td>
                <td className="px-4 py-2 text-gray-700">{rw.label}</td>
                <td className="px-4 py-2 text-right">{n(rw.debit) ? '₹' + fmt(rw.debit) : ''}</td>
                <td className="px-4 py-2 text-right">{n(rw.credit) ? '₹' + fmt(rw.credit) : ''}</td>
                <td className="px-4 py-2 text-right font-medium">₹{fmt(Math.abs(n(rw.balance)))} {n(rw.balance) >= 0 ? 'Dr' : 'Cr'}</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-400">No transactions</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ───────── SHARED UI ───────── */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-5 py-3 border-b"><h3 className="font-semibold text-gray-800">{title}</h3><button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button></div>
        <div className="p-5 space-y-3">{children}
          <style>{`.inp{width:100%;border:1px solid #d1d5db;border-radius:8px;padding:8px 10px;font-size:14px;outline:none}.inp:focus{border-color:#6366f1}`}</style>
        </div>
      </div>
    </div>
  );
}
const Field = ({ label, children }) => <label className="block"><span className="text-xs text-gray-500 mb-1 block">{label}</span>{children}</label>;

/* ───────── myBillBook-style PRINT ───────── */
function printInvoice(inv) {
  const ps = inv.party_snapshot || {};
  const inter = n(inv.igst) > 0;
  const rows = (inv.items || []).map((it, i) => `
    <tr>
      <td>${i + 1}</td>
      <td style="text-align:left">${esc(it.name)}${it.hsn ? `<br><span class="hsn">HSN: ${esc(it.hsn)}</span>` : ''}</td>
      <td>${nf(it.qty)} ${esc(it.unit || '')}</td>
      <td>₹${nf(it.price)}</td>
      <td>${n(it.discount) ? '₹' + nf(it.discount) : '—'}</td>
      <td>${nf(it.gst_rate)}%</td>
      <td style="text-align:right">₹${nf(it.amount)}</td>
    </tr>`).join('');
  const bal = r2(n(inv.grand_total) - n(inv.amount_received));
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(inv.invoice_no)}</title>
  <style>
    *{box-sizing:border-box;font-family:Arial,Helvetica,sans-serif}
    body{margin:0;padding:24px;color:#1f2937;font-size:13px}
    .hd{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #4f46e5;padding-bottom:12px}
    .biz h1{margin:0;font-size:22px;color:#4f46e5}
    .biz p{margin:2px 0;font-size:12px;color:#6b7280}
    .tag{background:#4f46e5;color:#fff;padding:4px 12px;border-radius:6px;font-weight:bold;font-size:14px}
    .meta{display:flex;justify-content:space-between;margin:16px 0}
    .box{font-size:12px;line-height:1.5}
    .box b{color:#374151}
    table{width:100%;border-collapse:collapse;margin-top:8px}
    th{background:#eef2ff;color:#4338ca;padding:8px;font-size:11px;text-align:center;border:1px solid #e5e7eb}
    td{padding:7px 8px;border:1px solid #e5e7eb;text-align:center;font-size:12px}
    .hsn{color:#9ca3af;font-size:10px}
    .tot{width:280px;margin-left:auto;margin-top:12px;font-size:13px}
    .tot div{display:flex;justify-content:space-between;padding:4px 0}
    .tot .g{border-top:2px solid #4f46e5;margin-top:6px;padding-top:8px;font-weight:bold;font-size:15px;color:#4f46e5}
    .ft{margin-top:28px;display:flex;justify-content:space-between;align-items:flex-end}
    .note{font-size:11px;color:#6b7280;max-width:60%}
    .sign{text-align:center;font-size:12px}
    .sign .line{border-top:1px solid #9ca3af;width:160px;margin-top:40px;padding-top:4px}
    @media print{body{padding:0}}
  </style></head><body>
    <div class="hd">
      <div class="biz"><h1>Avanaspace</h1><p>Product Sales Invoice</p></div>
      <div style="text-align:right"><span class="tag">TAX INVOICE</span><p style="margin-top:8px;font-size:12px"><b>${esc(inv.invoice_no)}</b><br>${esc(inv.invoice_date || '')}</p></div>
    </div>
    <div class="meta">
      <div class="box"><b>Bill To</b><br>${esc(ps.name || '')}<br>${ps.billing_address ? esc(ps.billing_address).replace(/\n/g, '<br>') + '<br>' : ''}${ps.mobile ? 'Mob: ' + esc(ps.mobile) + '<br>' : ''}${ps.gstin ? 'GSTIN: ' + esc(ps.gstin) : ''}</div>
      <div class="box" style="text-align:right"><b>Payment</b><br>Mode: ${esc(inv.payment_mode || 'Cash')}<br>Received: ₹${nf(inv.amount_received)}</div>
    </div>
    <table>
      <thead><tr><th>#</th><th>Item</th><th>Qty</th><th>Rate</th><th>Disc</th><th>GST</th><th>Amount</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="tot">
      <div><span>Taxable</span><span>₹${nf(inv.subtotal ? r2(n(inv.subtotal) - n(inv.discount_total)) : 0)}</span></div>
      ${n(inv.discount_total) ? `<div><span>Discount</span><span>₹${nf(inv.discount_total)}</span></div>` : ''}
      ${inter ? `<div><span>IGST</span><span>₹${nf(inv.igst)}</span></div>` : `<div><span>CGST</span><span>₹${nf(inv.cgst)}</span></div><div><span>SGST</span><span>₹${nf(inv.sgst)}</span></div>`}
      <div class="g"><span>Grand Total</span><span>₹${nf(inv.grand_total)}</span></div>
      <div><span>Received</span><span>₹${nf(inv.amount_received)}</span></div>
      <div style="font-weight:bold"><span>Balance Due</span><span>₹${nf(bal)}</span></div>
    </div>
    <div class="ft">
      <div class="note">${inv.notes ? esc(inv.notes).replace(/\n/g, '<br>') : ''}<br><br>This is a computer-generated invoice.</div>
      <div class="sign">For Avanaspace<div class="line">Authorised Signatory</div></div>
    </div>
    <script>window.onload=function(){window.print()}</script>
  </body></html>`;
  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); } else { toast.error('Allow pop-ups to print'); }
}
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }
function nf(v) { return n(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
